import { CameraController } from './controls/CameraController';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { Loader } from './utils/Loader';
import { InputHandler } from './input/InputHandler';
import { CameraType, CreatorStep } from '@/types/creator';
import { EventEmitter } from './utils/EventEmitter';
import {
	RendererMode,
	SerializedRenderer,
	ThreeEvent,
	ThreeEvents,
	UserDataObjectType,
	UserDataProp,
} from './types';
import { Exporter, ExportType } from './utils/Exporter';
import { LightController } from './controls/LightsController';
import { PlaygroundManager } from './managers/PlaygroundManager';
import { MeasurementManager } from './managers/MeasurementManager';
import { ProjectPlane } from './objects/ProjectPlane';
import { ModulesManager } from './managers/ModulesManager';
import { formatExactDate } from '@/utils/utilities';
import { TransformController } from './controls/TransformController';
import {
	Mesh,
	BufferGeometry,
	WebGLRenderer,
	Group,
	Scene,
	Color,
	PCFSoftShadowMap,
	SRGBColorSpace,
	LinearToneMapping,
	EquirectangularReflectionMapping,
} from 'three';
import {
	acceleratedRaycast,
	computeBoundsTree,
	disposeBoundsTree,
} from 'three-mesh-bvh';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import html2canvas from 'html2canvas';
import envMapPath from '@/assets/hdri/studio.exr';
import APP_CONFIG from '@/configs/appConfig';
import Stats from 'three/examples/jsm/libs/stats.module.js';

Mesh.prototype.raycast = acceleratedRaycast;
BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;

const stats = new Stats();
// if (import.meta.env.DEV) document.body.appendChild(stats.dom);

export class ProjectRenderer {
	private static instance: ProjectRenderer = new ProjectRenderer();
	private loader = Loader.getInstance();
	private exporter = new Exporter(this);
	private _mode = RendererMode.SHAPES;
	private css2DRenderer!: CSS2DRenderer;
	private screenshotCss2DRenderer!: CSS2DRenderer;
	initialized = false;
	isTouchDevice = false;
	renderer!: WebGLRenderer;
	screenshotRenderer!: WebGLRenderer;
	lightsController!: LightController;
	cameraController!: CameraController;
	inputHandler = new InputHandler(this);
	transformController!: TransformController;
	playgroundManager!: PlaygroundManager;
	modulesManager = new ModulesManager(this);
	measurementManager = new MeasurementManager(this);
	emitter = new EventEmitter<ThreeEvents>();
	loading = false;
	projectPlane = new ProjectPlane(this);
	project = new Group();
	scene!: Scene;
	canvas: HTMLCanvasElement | null = null;

	private constructor() {
		this.project.userData = {
			[UserDataProp.OBJECT_TYPE]: UserDataObjectType.PROJECT,
		};
	}

	static getInstance() {
		return ProjectRenderer.instance;
	}

	get mode() {
		return this._mode;
	}

	set mode(value: RendererMode) {
		this._mode = value;
		this.setAvailability();

		switch (value) {
			case RendererMode.CUSTOM_SHAPE_DRAWING:
				this.emitter.emit(ThreeEvent.IS_DRAWING, true);
				break;
			case RendererMode.MEASURING:
				this.cameraController.orthographicControls.enabled = false;
				break;
			default:
				this.cameraController.orthographicControls.enabled = true;
				this.emitter.emit(ThreeEvent.IS_DRAWING, false);
				break;
		}
	}

	private initRenderers() {
		this.renderer = new WebGLRenderer({
			canvas: this.canvas!,
			antialias: true,
		});

		this.scene.background = new Color(RENDERER_CONFIG.COLORS.LIGHT_CREAM);

		const offscreenCanvas = new OffscreenCanvas(
			RENDERER_CONFIG.SCREENSHOT.WIDTH,
			RENDERER_CONFIG.SCREENSHOT.HEIGHT
		);

		this.screenshotRenderer = new WebGLRenderer({
			canvas: offscreenCanvas,
			antialias: true,
			preserveDrawingBuffer: true,
		});

		for (const renderer of [this.renderer, this.screenshotRenderer]) {
			renderer.shadowMap.enabled = true;
			renderer.shadowMap.type = PCFSoftShadowMap;
			renderer.outputColorSpace = SRGBColorSpace;
			renderer.toneMapping = LinearToneMapping;
			renderer.toneMappingExposure = 1;
		}

		this.css2DRenderer = new CSS2DRenderer();
		this.screenshotCss2DRenderer = new CSS2DRenderer();

		for (const css2dRenderer of [
			this.css2DRenderer,
			this.screenshotCss2DRenderer,
		]) {
			css2dRenderer.domElement.style.pointerEvents = 'none';
			this.canvas!.parentElement!.appendChild(css2dRenderer.domElement);
		}

		this.css2DRenderer.domElement.style.zIndex =
			APP_CONFIG.Z_INDEX.CSS_2D_RENDERER.toString();
		this.css2DRenderer.domElement.style.position = 'absolute';
		this.css2DRenderer.domElement.style.top = '0px';

		this.screenshotCss2DRenderer.domElement.style.background = 'none';
		this.screenshotCss2DRenderer.domElement.style.position = 'fixed';
		this.screenshotCss2DRenderer.domElement.style.zIndex = '-1';

		this.screenshotCss2DRenderer.setSize(
			RENDERER_CONFIG.SCREENSHOT.WIDTH,
			RENDERER_CONFIG.SCREENSHOT.HEIGHT
		);

		this.renderer.setAnimationLoop(() => {
			if (import.meta.env.DEV) stats.begin();

			switch (this.cameraController.activeCamera) {
				case CameraType.ORTHOGRAPHIC:
					this.cameraController.orthographicControls.update();
					this.css2DRenderer.render(
						this.scene,
						this.cameraController.orthographicCamera
					);
					this.renderer.render(
						this.scene,
						this.cameraController.orthographicCamera
					);
					break;
				case CameraType.PERSPECTIVE:
					this.cameraController.perspectiveControls.update();
					this.css2DRenderer.render(
						this.scene,
						this.cameraController.perspectiveCamera
					);
					this.renderer.render(
						this.scene,
						this.cameraController.perspectiveCamera
					);
					break;

				default:
					throw new Error('UNKNOWN_CAMERA_TYPE');
			}

			if (import.meta.env.DEV) stats.end();
		});
	}

	private loadEnvMap() {
		this.loader.eXRLoader.load(envMapPath, (texture) => {
			texture.mapping = EquirectangularReflectionMapping;

			this.scene.environment = texture;
			this.scene.environmentIntensity = 0.4;
		});
	}

	init(canvas: HTMLCanvasElement, isTouchDevice: boolean) {
		this.canvas = canvas;
		this.isTouchDevice = isTouchDevice;

		this.scene = new Scene();

		this.scene.add(this.project);

		this.cameraController = new CameraController(this);
		this.lightsController = new LightController(this);

		this.inputHandler.init();

		this.projectPlane.createPlane();
		this.projectPlane.initGrid();

		this.loadEnvMap();
		this.initRenderers();

		this.playgroundManager = new PlaygroundManager(this);
		this.transformController = new TransformController(this);

		this.handleResize();
		this.initialized = true;
	}

	handleCreatorStepChange(step: CreatorStep) {
		switch (step) {
			case CreatorStep.SHAPES:
				this.mode = RendererMode.SHAPES;
				break;
			case CreatorStep.MODULES:
				this.mode = RendererMode.MODULES;
				break;
			case CreatorStep.CONTACT:
				this.mode = RendererMode.PREVIEW;
				break;
		}
	}

	setAvailability() {
		this.inputHandler.setAvailability();
		this.transformController.setAvailability();
		this.playgroundManager.setAvailability();
		this.modulesManager.setAvailability();
		this.measurementManager.setAvailability();
		this.inputHandler.cursorHandler.setCursorType();
	}

	async screenshot() {
		this.playgroundManager.prepareScreenshot();
		this.cameraController.prepareScreenshotCameras();

		this.screenshotRenderer.render(
			this.scene,
			this.cameraController.screenshotOrthographicCamera
		);
		this.screenshotCss2DRenderer.render(
			this.scene,
			this.cameraController.screenshotOrthographicCamera
		);

		let screenshotCanvas = this.screenshotRenderer
			.domElement as unknown as OffscreenCanvas;

		const finalCanvas = new OffscreenCanvas(
			screenshotCanvas.width,
			screenshotCanvas.height
		);
		const css2dCanvas = await html2canvas(
			this.screenshotCss2DRenderer.domElement,
			{
				logging: false,
				backgroundColor: null,
			}
		);

		const ctx = finalCanvas.getContext('2d');
		ctx!.drawImage(screenshotCanvas, 0, 0);
		ctx!.drawImage(css2dCanvas, 0, 0);

		let blob = await finalCanvas.convertToBlob();
		const orthoImageFile = new File(
			[blob],
			`Konfiguracja projektu ${formatExactDate()} rzut prostopadły.png`
		);

		const perspectiveImageFiles: File[] = [];

		const perspectiveCameras = this.cameraController
			.userScreenshotPerspectiveCameras.length
			? this.cameraController.userScreenshotPerspectiveCameras
			: this.cameraController.screenshotPerspectiveCameras;

		for (let index = 0; index < perspectiveCameras.length; index++) {
			const camera = perspectiveCameras[index];
			this.screenshotRenderer.render(this.scene, camera);
			screenshotCanvas = this.screenshotRenderer
				.domElement as unknown as OffscreenCanvas;
			blob = await screenshotCanvas.convertToBlob();

			const cameraName =
				camera.userData.name || `Kamera perspektywa ${index + 1}`;

			const file = new File(
				[blob],
				`Konfiguracja projektu ${formatExactDate()} - ${cameraName}.png`
			);

			perspectiveImageFiles.push(file);
		}

		this.playgroundManager.prepareScreenshot(true);

		return [orthoImageFile, ...perspectiveImageFiles];
	}

	handleCameraCenter() {
		this.cameraController.centerCamera();
	}

	handleCameraZoom(value: number) {
		this.cameraController.handleCameraZoom(value);
	}

	handleResize() {
		const { width, height } =
			this.canvas!.parentElement!.getBoundingClientRect();

		this.cameraController.handleResize(width / height);

		this.renderer.setSize(width, height);
		this.css2DRenderer.setSize(width, height);
	}

	async exportProjectModel(type: ExportType) {
		return await this.exporter.exportProject(type);
	}

	dispose() {
		this.renderer.dispose();
		this.cameraController.dispose();

		const ctx = this.canvas!.getContext('webgl2')!;
		ctx.clear(RENDERER_CONFIG.SCENE_BACKGROUND);
	}

	export(): SerializedRenderer {
		return {
			projectPlane: this.projectPlane.export(),
			playgroundManger: this.playgroundManager.export(),
			modulesManger: this.modulesManager.export(),
			measurementManager: this.measurementManager.export(),
			cameraController: this.cameraController.export(),
		};
	}

	loadFromData(data: SerializedRenderer) {
		this.projectPlane.loadFromData(data.projectPlane);
		this.playgroundManager.loadFromData(data.playgroundManger);
		this.modulesManager.loadFromData(data.modulesManger);
		this.measurementManager.loadFromData(data.measurementManager);
		this.cameraController.loadFromData(data.cameraController);
	}
}
